import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { formatTimeAgo } from '../utils/formatters'
import {
  Building2,
  Users,
  MessageSquare,
  Package,
  Flag,
  Key,
  Mail,
  Gamepad2,
  ArrowRight,
  TrendingUp,
  RefreshCw,
  TreePalm,
  Car,
  Clock,
  CalendarDays,
  ChevronRight,
  Circle,
  Bug
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import api, { announcementApi, deliveryApi, sentryApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'
import {
  Card,
  CardHeader,
  CardBody,
  GridContainer,
  Badge,
  LoadingSpinner,
  Button,
  ErrorMessage
} from '../components/common'

// ─── Skeleton Loader ────────────────────────────────────────────────────────

const Skeleton = ({ className = '' }) => (
  <div className={`bg-slate-100 rounded-lg animate-pulse ${className}`} />
)

// ─── Stat Card ──────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, href, color = 'primary', loading, delay = '0s' }) => {
  const themes = {
    primary: {
      iconBg: 'bg-gradient-to-br from-primary-500 to-primary-600',
      accent: 'bg-primary-500',
      ring: 'ring-primary-500/10',
    },
    accent: {
      iconBg: 'bg-gradient-to-br from-accent-500 to-accent-600',
      accent: 'bg-accent-500',
      ring: 'ring-accent-500/10',
    },
  }
  const theme = themes[color] || themes.primary

  return (
    <Link
      to={href}
      className="block group animate-login-fade-in opacity-0"
      style={{ animationDelay: delay }}
    >
      <div className="relative bg-white rounded-2xl border border-slate-100 p-5 shadow-soft transition-all duration-300 hover:shadow-lg hover:border-slate-200 hover:-translate-y-0.5 overflow-hidden">
        {/* Top accent line */}
        <div className={`absolute top-0 left-0 right-0 h-[2px] ${theme.accent} opacity-60`} />

        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-[0.8rem] font-medium text-dark-400 uppercase tracking-wider">{label}</p>
            <p className="text-[2rem] font-bold text-dark-900 font-display tracking-tight mt-1 leading-none">
              {loading ? <Skeleton className="w-14 h-8 mt-1" /> : value}
            </p>
          </div>
          <div className={`${theme.iconBg} p-2.5 rounded-xl shadow-sm flex-shrink-0 ring-4 ${theme.ring}`}>
            <Icon size={20} className="text-white" />
          </div>
        </div>

        <div className="mt-3 flex items-center text-xs text-dark-400 group-hover:text-primary-500 transition-colors">
          <span>View details</span>
          <ChevronRight size={13} className="ml-0.5 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  )
}

// ─── Delivery Ring Chart (CSS-only) ─────────────────────────────────────────

const DeliveryRing = ({ value, total, color, label, size = 64, strokeWidth = 5 }) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const percentage = total > 0 ? (value / total) * 100 : 0
  const offset = circumference - (percentage / 100) * circumference

  const colorMap = {
    yellow: { stroke: '#EAB308', bg: 'bg-yellow-50', text: 'text-yellow-700' },
    blue: { stroke: '#3B82F6', bg: 'bg-blue-50', text: 'text-blue-700' },
    green: { stroke: '#16A34A', bg: 'bg-green-50', text: 'text-green-700' },
    red: { stroke: '#EF4444', bg: 'bg-red-50', text: 'text-red-700' },
  }
  const c = colorMap[color] || colorMap.blue

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#F1F5F9"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={c.stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-sm font-bold ${c.text}`}>{value}</span>
        </div>
      </div>
      <span className="text-[0.7rem] font-medium text-dark-400 uppercase tracking-wider">{label}</span>
    </div>
  )
}

// ─── Activity Item ──────────────────────────────────────────────────────────

const ActivityItem = ({ title, subtitle, time, badge, href, icon: Icon, iconColor = 'primary' }) => {
  const iconColors = {
    primary: 'text-primary-500 bg-primary-50',
    accent: 'text-accent-600 bg-accent-50',
    warning: 'text-yellow-600 bg-yellow-50',
    danger: 'text-red-500 bg-red-50',
    info: 'text-blue-500 bg-blue-50',
  }
  const ic = iconColors[iconColor] || iconColors.primary

  return (
    <Link
      to={href}
      className="flex items-center gap-3 py-3 px-3 -mx-3 rounded-xl hover:bg-slate-50/80 transition-all duration-200 group"
    >
      {Icon && (
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${ic}`}>
          <Icon size={16} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-dark-800 truncate group-hover:text-dark-900">{title}</p>
        <p className="text-xs text-dark-400 mt-0.5 truncate">{subtitle}</p>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
        {badge}
        <span className="text-[0.7rem] text-dark-300 whitespace-nowrap">{time}</span>
      </div>
    </Link>
  )
}

// ─── Quick Action ───────────────────────────────────────────────────────────

const QuickAction = ({ icon: Icon, label, description, href, color = 'primary' }) => {
  const themes = {
    primary: { bg: 'bg-primary-50 group-hover:bg-primary-100', icon: 'text-primary-500', border: 'group-hover:border-primary-200' },
    accent: { bg: 'bg-accent-50 group-hover:bg-accent-100', icon: 'text-accent-600', border: 'group-hover:border-accent-200' },
  }
  const t = themes[color] || themes.primary

  return (
    <Link to={href} className="block group">
      <div className={`bg-white rounded-2xl border border-slate-100 p-4 shadow-soft transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 ${t.border} h-full`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${t.bg}`}>
            <Icon size={18} className={`${t.icon} transition-colors`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-dark-800 group-hover:text-dark-900 transition-colors truncate">{label}</h3>
            <p className="text-xs text-dark-400 mt-0.5 truncate">{description}</p>
          </div>
          <ChevronRight size={15} className="text-dark-200 group-hover:text-dark-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
        </div>
      </div>
    </Link>
  )
}

// ─── Utility ────────────────────────────────────────────────────────────────

const formatRelativeTime = formatTimeAgo

// ─── Dashboard ──────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user, isAdmin } = useAuth()
  const [stats, setStats] = useState({ users: null, buildings: null })
  const [announcements, setAnnouncements] = useState([])
  const [deliveries, setDeliveries] = useState([])
  const [deliveryStats, setDeliveryStats] = useState(null)
  const [sentryIssues, setSentryIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadDashboardData = async () => {
    setLoading(true)
    setError(null)

    const promises = [
      api.get('/Building', { params: { pageNumber: 1, pageSize: 1 } }),
      announcementApi.list({ pageNumber: 1, pageSize: 5 }),
      deliveryApi.list({ pageNumber: 1, pageSize: 5 }),
      deliveryApi.getStats(),
    ]

    // Only fetch user stats and sentry issues for admins
    if (isAdmin) {
      promises.push(api.get('/Users', { params: { pageNumber: 1, pageSize: 1 } }))
      promises.push(sentryApi.getIssues({ query: 'is:unresolved', sort: 'date' }))
    }

    const results = await Promise.allSettled(promises)

    const [buildingsRes, announcementsRes, deliveriesRes, deliveryStatsRes, usersRes, sentryRes] = results

    if (buildingsRes.status === 'fulfilled') {
      setStats(prev => ({ ...prev, buildings: buildingsRes.value.data.totalCount ?? 0 }))
    }
    if (announcementsRes.status === 'fulfilled') {
      const data = announcementsRes.value.data
      setAnnouncements(data.items || [])
      setStats(prev => ({ ...prev, announcements: data.totalCount ?? 0 }))
    }
    if (deliveriesRes.status === 'fulfilled') {
      const data = deliveriesRes.value.data
      setDeliveries(data.items || [])
      setStats(prev => ({ ...prev, deliveries: data.totalCount ?? 0 }))
    }
    if (deliveryStatsRes.status === 'fulfilled') {
      setDeliveryStats(deliveryStatsRes.value.data)
    }
    if (usersRes?.status === 'fulfilled') {
      setStats(prev => ({ ...prev, users: usersRes.value.data.totalCount ?? 0 }))
    }
    if (sentryRes?.status === 'fulfilled') {
      setSentryIssues((sentryRes.value.data || []).slice(0, 5))
    }
    const allFailed = results.every(r => r.status === 'rejected')
    if (allFailed) {
      const firstRejected = results.find(r => r.status === 'rejected')
      setError(extractErrorMessage(firstRejected?.reason))
    }

    setLoading(false)
  }

  useEffect(() => {
    loadDashboardData()
  }, [])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  const getPriorityVariant = (priority) => {
    if (priority === 1) return 'danger'
    if (priority === 2) return 'warning'
    return 'default'
  }

  const getPriorityLabel = (priority) => {
    if (priority === 1) return 'High'
    if (priority === 2) return 'Medium'
    return 'Low'
  }

  const getDeliveryStatusVariant = (status) => {
    const map = { 0: 'warning', 1: 'info', 2: 'success', 3: 'danger' }
    return map[status] || 'default'
  }

  const getDeliveryStatusLabel = (status) => {
    const map = { 0: 'Pending', 1: 'Delivered', 2: 'Collected', 3: 'Returned' }
    return map[status] || 'Unknown'
  }

  const deliveryTotal = useMemo(() => {
    if (!deliveryStats) return 0
    return (
      (deliveryStats.pendingCount ?? deliveryStats.pending ?? 0) +
      (deliveryStats.deliveredCount ?? deliveryStats.delivered ?? 0) +
      (deliveryStats.collectedCount ?? deliveryStats.collected ?? 0) +
      (deliveryStats.overdueCount ?? deliveryStats.overdue ?? 0)
    )
  }, [deliveryStats])

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  if (loading && stats.users === null) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-8">

      {/* ─── Hero Greeting ─────────────────────────────────────────── */}
      <div
        className="relative rounded-2xl overflow-hidden animate-login-fade-in opacity-0"
        style={{ animationDelay: '0s' }}
      >
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #1a2636 100%)',
          }}
        />
        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, #94A3B8 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        />
        {/* Orange accent glow */}
        <div
          className="absolute -top-20 -right-20 w-72 h-72 opacity-[0.07] rounded-full"
          style={{ background: 'radial-gradient(circle, #FF6F0F, transparent 70%)' }}
        />

        <div className="relative z-10 px-8 py-7 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-white">
              {getGreeting()}, {user?.firstName || user?.name || 'Admin'}
            </h1>
            <p className="text-dark-400 mt-1.5 text-sm flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <CalendarDays size={13} className="text-dark-500" />
                {currentDate}
              </span>
              <span className="text-dark-600">|</span>
              <span>{isAdmin ? 'Platform overview' : 'Building overview'}</span>
            </p>
          </div>

          <Button
            onClick={loadDashboardData}
            variant="ghost"
            size="small"
            icon={<RefreshCw size={15} className={loading ? 'animate-spin' : ''} />}
            disabled={loading}
            className="!text-dark-300 hover:!text-white hover:!bg-white/10 border border-dark-700 hover:border-dark-500"
          >
            Refresh
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {/* ─── Stat Cards ────────────────────────────────────────────── */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-5`}>
        {isAdmin && (
          <StatCard
            icon={Users}
            label="Total Users"
            value={stats.users ?? '—'}
            href="/users"
            color="primary"
            loading={loading && stats.users === null}
            delay="0.05s"
          />
        )}
        <StatCard
          icon={Building2}
          label="Buildings"
          value={stats.buildings ?? '—'}
          href="/buildings"
          color="accent"
          loading={loading && stats.buildings === null}
          delay="0.1s"
        />
        <StatCard
          icon={MessageSquare}
          label="Announcements"
          value={stats.announcements ?? '—'}
          href="/announcements"
          color="primary"
          loading={loading && stats.announcements === null}
          delay="0.15s"
        />
        <StatCard
          icon={Package}
          label="Deliveries"
          value={stats.deliveries ?? '—'}
          href="/deliveries"
          color="accent"
          loading={loading && stats.deliveries === null}
          delay="0.2s"
        />
      </div>

      {/* ─── Delivery Overview with Ring Charts ────────────────────── */}
      {deliveryStats && (
        <div className="animate-login-fade-in opacity-0" style={{ animationDelay: '0.25s' }}>
          <Card padding="none">
            <div className="p-6 pb-0 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-dark-900 font-display tracking-tight">Delivery Overview</h3>
                <p className="text-xs text-dark-400 mt-0.5">{deliveryTotal} total deliveries tracked</p>
              </div>
              <Button to="/deliveries/stats" variant="ghost" size="small" icon={<TrendingUp size={14} />}>
                View Stats
              </Button>
            </div>
            <div className="p-6 pt-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <DeliveryRing
                  value={deliveryStats.pendingCount ?? deliveryStats.pending ?? 0}
                  total={deliveryTotal}
                  color="yellow"
                  label="Pending"
                />
                <DeliveryRing
                  value={deliveryStats.deliveredCount ?? deliveryStats.delivered ?? 0}
                  total={deliveryTotal}
                  color="blue"
                  label="Delivered"
                />
                <DeliveryRing
                  value={deliveryStats.collectedCount ?? deliveryStats.collected ?? 0}
                  total={deliveryTotal}
                  color="green"
                  label="Collected"
                />
                <DeliveryRing
                  value={deliveryStats.overdueCount ?? deliveryStats.overdue ?? 0}
                  total={deliveryTotal}
                  color="red"
                  label="Overdue"
                />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ─── Activity Panels ───────────────────────────────────────── */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-login-fade-in opacity-0"
        style={{ animationDelay: '0.3s' }}
      >
        {/* Recent Announcements */}
        <Card padding="none">
          <div className="p-6 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                <MessageSquare size={15} className="text-primary-500" />
              </div>
              <h3 className="text-sm font-semibold text-dark-900 tracking-tight">Recent Announcements</h3>
            </div>
            <Button to="/announcements" variant="ghost" size="small">
              View All <ArrowRight size={13} className="ml-1" />
            </Button>
          </div>
          <div className="px-6 pb-5">
            {announcements.length === 0 ? (
              <div className="py-8 text-center">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-2">
                  <MessageSquare size={18} className="text-dark-300" />
                </div>
                <p className="text-sm text-dark-400">No announcements yet</p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {announcements.map((a) => (
                  <ActivityItem
                    key={a.announcementId || a.id}
                    title={a.title || 'Untitled'}
                    subtitle={a.buildingName || 'All buildings'}
                    time={formatRelativeTime(a.createDateTimeUtc || a.createdDate)}
                    icon={MessageSquare}
                    iconColor={a.priority === 1 ? 'danger' : a.priority === 2 ? 'warning' : 'primary'}
                    badge={
                      <Badge variant={getPriorityVariant(a.priority)} size="small">
                        {getPriorityLabel(a.priority)}
                      </Badge>
                    }
                    href={`/announcements/${a.announcementId || a.id}`}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Recent Deliveries */}
        <Card padding="none">
          <div className="p-6 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
                <Package size={15} className="text-accent-600" />
              </div>
              <h3 className="text-sm font-semibold text-dark-900 tracking-tight">Recent Deliveries</h3>
            </div>
            <Button to="/deliveries" variant="ghost" size="small">
              View All <ArrowRight size={13} className="ml-1" />
            </Button>
          </div>
          <div className="px-6 pb-5">
            {deliveries.length === 0 ? (
              <div className="py-8 text-center">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-2">
                  <Package size={18} className="text-dark-300" />
                </div>
                <p className="text-sm text-dark-400">No deliveries yet</p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {deliveries.map((d) => (
                  <ActivityItem
                    key={d.deliveryId || d.id}
                    title={d.description || d.trackingNumber || 'Delivery'}
                    subtitle={d.buildingName || d.recipientName || '—'}
                    time={formatRelativeTime(d.createDateTimeUtc || d.createdDate || d.deliveryDate)}
                    icon={Package}
                    iconColor={d.status === 0 ? 'warning' : d.status === 2 ? 'accent' : d.status === 3 ? 'danger' : 'info'}
                    badge={
                      <Badge variant={getDeliveryStatusVariant(d.status)} size="small">
                        {getDeliveryStatusLabel(d.status)}
                      </Badge>
                    }
                    href={`/deliveries/${d.deliveryId || d.id}`}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ─── Recent Errors ──────────────────────────────────────────── */}
      {isAdmin && sentryIssues.length > 0 && (
        <div
          className="animate-login-fade-in opacity-0"
          style={{ animationDelay: '0.32s' }}
        >
          <Card padding="none">
            <div className="p-6 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <Bug size={15} className="text-red-500" />
                </div>
                <h3 className="text-sm font-semibold text-dark-900 tracking-tight">Recent Errors</h3>
              </div>
              <Button to="/sentry-issues" variant="ghost" size="small">
                View All <ArrowRight size={13} className="ml-1" />
              </Button>
            </div>
            <div className="px-6 pb-5">
              <div className="space-y-0.5">
                {sentryIssues.map((issue) => (
                  <ActivityItem
                    key={issue.issueId}
                    title={issue.title || 'Unknown error'}
                    subtitle={`${issue.projectName}${issue.culprit ? ` \u00b7 ${issue.culprit}` : ''}`}
                    time={formatRelativeTime(issue.lastSeen)}
                    icon={Bug}
                    iconColor="danger"
                    badge={
                      <Badge
                        variant={issue.level === 'fatal' || issue.level === 'error' ? 'danger' : issue.level === 'warning' ? 'warning' : 'default'}
                        size="small"
                      >
                        {issue.level}
                      </Badge>
                    }
                    href={`/sentry-issues/${issue.issueId}`}
                  />
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ─── Quick Actions ─────────────────────────────────────────── */}
      <div
        className="animate-login-fade-in opacity-0"
        style={{ animationDelay: '0.35s' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-dark-800 uppercase tracking-wider">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isAdmin && (
            <QuickAction
              icon={Building2}
              label="New Building"
              description="Register a building"
              href="/buildings/create"
              color="accent"
            />
          )}
          {isAdmin && (
            <QuickAction
              icon={Users}
              label="New User"
              description="Create user account"
              href="/users/create"
              color="primary"
            />
          )}
          <QuickAction
            icon={MessageSquare}
            label="New Announcement"
            description="Send an announcement"
            href="/announcements/create"
            color="primary"
          />
          <QuickAction
            icon={Package}
            label="New Delivery"
            description="Log a delivery"
            href="/deliveries/create"
            color="accent"
          />
          {isAdmin && (
            <QuickAction
              icon={Key}
              label="Licenses"
              description="Manage licenses"
              href="/licenses"
              color="primary"
            />
          )}
          <QuickAction
            icon={TreePalm}
            label="Amenities"
            description="Manage amenities"
            href="/amenities"
            color="accent"
          />
          <QuickAction
            icon={Car}
            label="Parking Spots"
            description="Manage parking"
            href="/parking"
            color="primary"
          />
          {isAdmin && (
            <QuickAction
              icon={Flag}
              label="Feature Flags"
              description="Toggle features"
              href="/feature-flags"
              color="accent"
            />
          )}
          {isAdmin && (
            <QuickAction
              icon={Mail}
              label="Email Templates"
              description="Edit email content"
              href="/email-templates"
              color="primary"
            />
          )}
          {isAdmin && (
            <QuickAction
              icon={Gamepad2}
              label="Demo Generator"
              description="Generate demo data"
              href="/demo"
              color="accent"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
